import { forwardRef, useEffect, useRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { useFormField } from '../FormField';
import './Checkbox.css';

/**
 * Checkbox — `type="checkbox"` **71회 / 45파일** 을 흡수한다 (디자인 정본 B5).
 *
 * ★★ **네이티브 `<input>` 을 `display:none` 으로 숨기지 않는다.** 숨기면 키보드와 스크린리더가
 *    그것을 못 찾는다. `opacity:0` 로 **겹쳐 두고** 그림은 그 위에 그린다 —
 *    포커스도 클릭도 네이티브가 그대로 처리한다.
 *
 * ★★★ **`indeterminate` 는 property 라 JSX 로 못 준다.** 「일부 선택」은 DOM 에 직접 써야 한다
 *    (ref + effect). 전체선택 체크박스가 이걸 안 쓰면 「하나도 안 골랐다」와
 *    「몇 개만 골랐다」가 **같게 보인다**.
 */
export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  /** 일부만 선택된 상태. `checked` 보다 우선해 그려진다. */
  indeterminate?: boolean;
  invalid?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, indeterminate = false, invalid, className, id, disabled, required, 'aria-describedby': ariaDescribedBy, ...rest },
  ref,
) {
  const field = useFormField();
  const inner = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const controlId = id ?? field?.controlId;
  const isInvalid = invalid ?? field?.invalid ?? false;
  const isDisabled = disabled ?? field?.disabled ?? false;
  const isRequired = required ?? field?.required ?? false;
  const describedBy = [ariaDescribedBy, field?.describedBy].filter(Boolean).join(' ') || undefined;

  const cls = [
    'wc-check',
    isDisabled ? 'wc-check--disabled' : '',
    isInvalid ? 'wc-check--invalid' : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <label className={cls}>
      <input
        ref={(node) => {
          inner.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }}
        type="checkbox"
        id={controlId}
        className="wc-check__input"
        disabled={isDisabled}
        required={isRequired || undefined}
        aria-required={isRequired || undefined}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        {...rest}
      />
      <span className="wc-check__box" aria-hidden>
        {/* 체크와 「일부 선택」 막대를 한 svg 에 두고 CSS 로 가른다 — 상태마다 다른 그림을
            끼우면 전환이 튄다. 이모지·글자를 쓰지 않는다(정본 규칙). */}
        <svg viewBox="0 0 16 16" className="wc-check__mark">
          <path className="wc-check__tick" d="M3.5 8.4 L6.6 11.4 L12.5 5" fill="none" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
          <path className="wc-check__dash" d="M4 8 H12" fill="none" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </span>
      {label != null && <span className="wc-check__label">{label}</span>}
    </label>
  );
});
